import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { COURIER_TASK_KEYS } from '../lib';
import { CourierTaskApi } from '../api';
import type {
  CourierTaskBase,
  CourierTaskDetail,
  CourierTaskListParams,
  CourierTaskPaymentAcceptPayload,
  CourierTaskReturnAcceptPayload,
} from './types';

export const useCourierTaskList = (params?: CourierTaskListParams) => {
  return useQuery<ApiResponseList<CourierTaskBase>>({
    queryKey: COURIER_TASK_KEYS.list(params),
    queryFn: () => CourierTaskApi.getList(params),
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useCourierTaskById = (id: number, type: number) => {
  return useQuery<ApiResponse<CourierTaskDetail>>({
    queryKey: COURIER_TASK_KEYS.detail(id, type),
    queryFn: () => CourierTaskApi.getById(id, type),
    enabled: !!id && !!type,
  });
};

export const useAcceptCourierTaskPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CourierTaskPaymentAcceptPayload) => CourierTaskApi.paymentAccept(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COURIER_TASK_KEYS.all, refetchType: 'active' });
    },
  });
};

export const useAcceptCourierTaskReturn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CourierTaskReturnAcceptPayload) => CourierTaskApi.returnAccept(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: COURIER_TASK_KEYS.all, refetchType: 'active' });
    },
  });
};
